import React from "react";
import "../style/About.css";
import { motion } from "motion/react";
import {
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    AreaChart,
    Area,
    ResponsiveContainer,
} from "recharts";


const PhoneIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);


const MailIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 6-10 7L2 6" />
    </svg>
);


const PinIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);


const CheckIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 6 9 17l-5-5" />
    </svg>
);


const FEATURES = [
    "Our kitchen chimneys are designed to eliminate smoke and odours, providing a fresh and clean cooking environment.",
    "Our cooktop stoves are designed for efficiency and ease of use, making your cooking experience enjoyable.",
    "The gas hobs from Bronzer are a perfect blend of style and functionality, designed to make your kitchen look modern and sleek.",
    "We believe in delivering products that enhance your kitchen experience. Trust us for quality, reliability, and excellence.",
    "Kitchen appliances that simplify your life and suit your needs, while enhancing your kitchen design.",
    "No more smudges on the ceiling — smart kitchen appliances that auto-clean themselves.",
];


const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    return (
        <div className="chartTooltip">
            <span className="chartTooltipYear">
                {label}
            </span>

            <span className="chartTooltipValue">
                {payload[0].value}
            </span>
        </div>
    );
};


const About = () => {

    const chartData = [
        { name: "2020", uv: 5 },
        { name: "2021", uv: 14 },
        { name: "2022", uv: 18 },
        { name: "2023", uv: 20 },
        { name: "2024", uv: 22 },
        { name: "2025", uv: 25 },
    ];


    return (
        <section className="aboutBox" id="aboutUs">

            {/* HEADER */}

            <div className="aboutHeader">

                <span className="aboutEyebrow">
                    About us
                </span>

                <h2 className="aboutTitle">

                    <span className="aboutTitleLight">
                        Who
                    </span>

                    <span className="aboutTitleBold">
                        We are
                    </span>

                </h2>

            </div>


            {/* ABOUT GRID */}

            <div className="aboutGrid">

                {/* FOUNDER CARD */}

                <motion.div
                    className="founderCard"

                    initial={{
                        opacity: 0,
                        x: -60,
                    }}

                    whileInView={{
                        opacity: 1,
                        x: 0,
                    }}

                    viewport={{
                        once: true,
                        margin: "-80px",
                    }}

                    transition={{
                        duration: 0.8,
                        ease: "easeOut",
                    }}
                >

                    <span className="founderEyebrow">
                        Founder
                    </span>

                    <h3 className="founderName">
                        Ashish Sata
                    </h3>

                    <p className="founderCompany">
                        Sata International
                    </p>


                    <div className="founderContact">

                        <a
                            className="founderContactRow"
                            href="tel:+919898540840"
                        >
                            <span className="founderIcon">
                                <PhoneIcon />
                            </span>

                            +91 98985 40840
                        </a>


                        <a
                            className="founderContactRow"
                            href="mailto:sata.inernational91@gmail.com"
                        >
                            <span className="founderIcon">
                                <MailIcon />
                            </span>

                            sata.inernational91@gmail.com
                        </a>


                        <div className="founderContactRow founderAddress">

                            <span className="founderIcon">
                                <PinIcon />
                            </span>

                            Plot No. 37, Valinath Society,
                            U-M Road, Bhatar, Surat 395001,
                            Gujarat, India

                        </div>

                    </div>


                    <blockquote className="founderQuote">

                        <span
                            className="founderQuoteMark"
                            aria-hidden="true"
                        >
                            &ldquo;
                        </span>

                        A clean kitchen begins with fresh air&nbsp;&mdash;
                        our chimneys ensure a smoke-free, odourless
                        cooking experience every day.

                        <footer>
                            &mdash; Ashish Sata
                        </footer>

                    </blockquote>

                </motion.div>


                {/* BRONZER DETAILS */}

                <motion.div
                    className="bronzerDetails"

                    initial={{
                        opacity: 0,
                        x: 60,
                    }}

                    whileInView={{
                        opacity: 1,
                        x: 0,
                    }}

                    viewport={{
                        once: true,
                        margin: "-80px",
                    }}

                    transition={{
                        duration: 0.8,
                        ease: "easeOut",
                        delay: 0.15,
                    }}
                >

                    <span className="bronzerEyebrow">
                        Why choose Bronzer
                    </span>


                    <p className="aboutLead">
                        Bronzer, recognised as one of India&rsquo;s
                        leading kitchen chimney brands, is a trusted
                        name in kitchen appliances &mdash; built on a
                        commitment to quality, innovation, and design.
                    </p>


                    <ul className="aboutFeatureList">

                        {FEATURES.map((feature, i) => (

                            <motion.li
                                key={i}

                                initial={{
                                    opacity: 0,
                                    y: 14,
                                }}

                                whileInView={{
                                    opacity: 1,
                                    y: 0,
                                }}

                                viewport={{
                                    once: true,
                                }}

                                transition={{
                                    duration: 0.5,
                                    delay: 0.1 + i * 0.06,
                                }}
                            >

                                <span className="aboutFeatureCheck">
                                    <CheckIcon />
                                </span>

                                <span>
                                    {feature}
                                </span>

                            </motion.li>

                        ))}

                    </ul>

                </motion.div>

            </div>


            {/* SALES CARD */}

            <motion.div
                className="salesCard"

                initial={{
                    opacity: 0,
                    y: 40,
                }}

                whileInView={{
                    opacity: 1,
                    y: 0,
                }}

                viewport={{
                    once: true,
                    margin: "-80px",
                }}

                transition={{
                    duration: 0.8,
                    ease: "easeOut",
                }}
            >

                <div className="salesCardHeader">

                    <span className="salesEyebrow">
                        Growth
                    </span>

                    <h3 className="salesTitle">
                        Our successful sales over the past five years
                    </h3>

                </div>


                <div className="chart">

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <AreaChart
                            data={chartData}
                            margin={{
                                top: 10,
                                right: 16,
                                bottom: 0,
                                left: -16,
                            }}
                        >

                            <defs>

                                <linearGradient
                                    id="salesFill"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor="#e2843f"
                                        stopOpacity={0.35}
                                    />

                                    <stop
                                        offset="100%"
                                        stopColor="#e2843f"
                                        stopOpacity={0}
                                    />

                                </linearGradient>

                            </defs>


                            <CartesianGrid
                                stroke="rgba(255,255,255,0.08)"
                                vertical={false}
                            />


                            <XAxis
                                dataKey="name"
                                stroke="#9599a6"
                                tickLine={false}
                                axisLine={false}
                                tick={{
                                    fontFamily:
                                        "'JetBrains Mono', monospace",
                                    fontSize: 12,
                                }}
                            />


                            <YAxis
                                stroke="#9599a6"
                                tickLine={false}
                                axisLine={false}
                                tick={{
                                    fontFamily:
                                        "'JetBrains Mono', monospace",
                                    fontSize: 12,
                                }}
                            />


                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{
                                    stroke: "#e2843f",
                                    strokeWidth: 1,
                                    strokeDasharray: "4 4",
                                }}
                            />


                            <Area
                                type="monotone"
                                dataKey="uv"
                                stroke="#e2843f"
                                strokeWidth={2.5}
                                fill="url(#salesFill)"
                                dot={{
                                    r: 4,
                                    fill: "#e2843f",
                                    strokeWidth: 0,
                                }}
                                activeDot={{
                                    r: 6,
                                    fill: "#e2843f",
                                }}
                            />

                        </AreaChart>

                    </ResponsiveContainer>

                </div>

            </motion.div>

        </section>
    );
};


export default About;